import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Crown, Shield, Star, Globe, Truck, CreditCard, Users, Award, 
  CheckCircle, ArrowRight, Zap, TrendingUp, Target, MessageCircle,
  HeadphonesIcon, Briefcase, Building, Package, DollarSign, 
  BarChart3, Clock, FileText, Plane, MapPin, Phone, Mail, Bot,
  Upload, Search, Eye, Handshake, ShoppingCart, Languages,
  Home, Banknote, UserCheck, AlertTriangle, Headphones
} from 'lucide-react';

const BecomeExclusivePage = () => {
  const [activeTab, setActiveTab] = useState<'seller' | 'buyer'>('seller');

  const sellerFeatures = [
    {
      icon: Shield,
      title: 'Verified Seller Badge',
      description: 'Boosts credibility; appears on all listings to build instant trust with buyers',
      color: 'text-green-600 bg-green-100'
    },
    {
      icon: Star,
      title: 'Featured Listings',
      description: 'Higher visibility on homepage/category pages with priority placement',
      color: 'text-yellow-600 bg-yellow-100'
    },
    {
      icon: Bot,
      title: '24/7 AI Inquiry Assistant',
      description: 'Auto-replies to buyer inquiries when sellers are offline, never miss a lead',
      color: 'text-blue-600 bg-blue-100'
    },
    {
      icon: Upload,
      title: 'Unlimited Product Uploads',
      description: 'Remove limits on listing count - showcase your entire product catalog',
      color: 'text-purple-600 bg-purple-100'
    },
    {
      icon: Search,
      title: 'Priority Matching in RFQs',
      description: 'Get leads before non-premium sellers with first access to buyer requests',
      color: 'text-orange-600 bg-orange-100'
    },
    {
      icon: CreditCard,
      title: 'Escrow Integration Support',
      description: 'Enable safe payment flows with built-in escrow protection services',
      color: 'text-indigo-600 bg-indigo-100'
    },
    {
      icon: Plane,
      title: 'Buyers Hospitality Services',
      description: 'Includes airport pickup for visiting buyers and VIP treatment services',
      color: 'text-red-600 bg-red-100'
    },
    {
      icon: Eye,
      title: 'Pre-shipment Inspection Assistance',
      description: 'Partner with inspection agencies for quality assurance and buyer confidence',
      color: 'text-teal-600 bg-teal-100'
    },
    {
      icon: Package,
      title: 'Sample Forwarding Management',
      description: 'Logistics and tracking for product samples with professional handling',
      color: 'text-pink-600 bg-pink-100'
    },
    {
      icon: BarChart3,
      title: 'Analytics Dashboard',
      description: 'Product views, buyer behavior, and market trends with actionable insights',
      color: 'text-cyan-600 bg-cyan-100'
    },
    {
      icon: HeadphonesIcon,
      title: 'Dedicated Account Manager',
      description: 'Support for exports, documents, marketing with personal assistance',
      color: 'text-violet-600 bg-violet-100'
    },
    {
      icon: DollarSign,
      title: 'Faster Payment Withdrawal',
      description: 'Speed up payout cycles with lower fees and priority processing',
      color: 'text-emerald-600 bg-emerald-100'
    }
  ];

  const buyerFeatures = [
    {
      icon: Eye,
      title: 'Pre-shipment Inspection',
      description: 'Independent QC before shipment to ensure quality meets your standards',
      color: 'text-blue-600 bg-blue-100'
    },
    {
      icon: Shield,
      title: 'Escrow Payment Protection',
      description: 'Funds held until buyer confirms satisfaction - complete peace of mind',
      color: 'text-green-600 bg-green-100'
    },
    {
      icon: ShoppingCart,
      title: '"Buy for Me" Concierge',
      description: 'AI + human sourcing on buyer\'s behalf for complex procurement needs',
      color: 'text-purple-600 bg-purple-100'
    },
    {
      icon: Package,
      title: 'Sample Ordering Support',
      description: 'Hassle-free sampling from verified suppliers with tracking and logistics',
      color: 'text-orange-600 bg-orange-100'
    },
    {
      icon: Languages,
      title: 'Multilingual Buyer Assistant',
      description: 'Real-time translation support during chats/calls for seamless communication',
      color: 'text-indigo-600 bg-indigo-100'
    },
    {
      icon: Home,
      title: 'Factory Visit Coordination',
      description: 'Includes travel, accommodation, and pickup for supplier facility visits',
      color: 'text-red-600 bg-red-100'
    },
    {
      icon: Banknote,
      title: 'LC/TT Assistance',
      description: 'Help with trade financing & letters of credit from banking partners',
      color: 'text-yellow-600 bg-yellow-100'
    },
    {
      icon: Target,
      title: 'Priority Seller Matching',
      description: 'Get matched with best-performing suppliers first based on your requirements',
      color: 'text-teal-600 bg-teal-100'
    },
    {
      icon: UserCheck,
      title: 'Verified Exporters Access Only',
      description: 'Reduce scams and middlemen with access to pre-verified supplier network',
      color: 'text-pink-600 bg-pink-100'
    },
    {
      icon: AlertTriangle,
      title: 'Buyer Protection Guarantee',
      description: 'Policy-backed resolution process for disputes with full money-back guarantee',
      color: 'text-cyan-600 bg-cyan-100'
    }
  ];

  const sellerPlans = [
    {
      name: 'Standard',
      price: 'Free',
      period: '',
      description: 'Basic listing features for new sellers',
      features: [
        'Basic product listings (up to 50)',
        'Standard search visibility',
        'Email support',
        'Basic analytics',
        'Standard payment processing',
        'Manual inquiry responses'
      ],
      cta: 'Get Started',
      popular: false,
      color: 'border-gray-300'
    },
    {
      name: 'Premium',
      price: '$199',
      period: '/month',
      description: 'Enhanced features for growing businesses',
      features: [
        'Enhanced product listings (up to 500)',
        'Featured placement in categories',
        'Phone & email support',
        'Advanced analytics dashboard',
        'Faster payment processing',
        'Basic AI inquiry assistant',
        'Sample forwarding support',
        'Marketing tools access'
      ],
      cta: 'Upgrade Now',
      popular: false,
      color: 'border-blue-500'
    },
    {
      name: 'Exclusive',
      price: '$499',
      period: '/month',
      description: 'Complete solution for serious exporters',
      features: [
        'Verified Seller Badge',
        'Featured Listings Priority',
        '24/7 AI Inquiry Assistant',
        'Unlimited Product Uploads',
        'Priority Matching in RFQs',
        'Escrow Integration Support',
        'Buyers Hospitality Services',
        'Pre-shipment Inspection Assistance',
        'Sample Forwarding Management',
        'Advanced Analytics Dashboard',
        'Dedicated Account Manager',
        'Faster Payment Withdrawal'
      ],
      cta: 'Become Exclusive',
      popular: true,
      color: 'border-orange-500'
    }
  ];

  const buyerPlans = [
    {
      name: 'Basic',
      price: 'Free',
      period: '',
      description: 'Essential features for small businesses',
      features: [
        'Access to all suppliers',
        'Basic search filters',
        'Email support',
        'Standard order processing',
        'Basic market data',
        'Manual communication'
      ],
      cta: 'Start Buying',
      popular: false,
      color: 'border-gray-300'
    },
    {
      name: 'Professional',
      price: '$299',
      period: '/month',
      description: 'Advanced tools for serious buyers',
      features: [
        'Verified supplier access',
        'Advanced search & filters',
        'Priority support',
        'Sample ordering support',
        'Market intelligence reports',
        'Bulk order discounts',
        'Basic translation support',
        'Trade finance guidance'
      ],
      cta: 'Upgrade Now',
      popular: false,
      color: 'border-blue-500'
    },
    {
      name: 'Exclusive',
      price: '$799',
      period: '/month',
      description: 'Complete solution for enterprise buyers',
      features: [
        'Pre-shipment Inspection',
        'Escrow Payment Protection',
        '"Buy for Me" Concierge',
        'Sample Ordering Support',
        'Multilingual Buyer Assistant',
        'Factory Visit Coordination',
        'LC/TT Assistance',
        'Priority Seller Matching',
        'Verified Exporters Access Only',
        'Buyer Protection Guarantee'
      ],
      cta: 'Become Exclusive',
      popular: true,
      color: 'border-orange-500'
    }
  ];

  const trustIndicators = [
    { icon: Shield, text: 'ISO 27001 Certified', subtext: 'Data Security' },
    { icon: Award, text: 'Export Excellence Award', subtext: '2024 Winner' },
    { icon: Users, text: '10,000+ Active Users', subtext: 'Trusted Globally' },
    { icon: Globe, text: '50+ Countries', subtext: 'Global Reach' },
    { icon: DollarSign, text: '$500M+ Trade Volume', subtext: 'Annual Transactions' },
    { icon: CheckCircle, text: '99.9% Uptime', subtext: 'Reliable Platform' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-orange-600 via-red-600 to-purple-700 text-white py-20 overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-32 h-32 bg-white/10 rounded-full blur-xl"></div>
          <div className="absolute bottom-10 right-10 w-48 h-48 bg-white/10 rounded-full blur-xl"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-white/5 rounded-full blur-2xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center mb-6">
            <Crown className="h-16 w-16 text-yellow-300 mr-4" />
            <h1 className="text-5xl md:text-7xl font-bold">
              Become <span className="text-yellow-300">Exclusive</span>
            </h1>
          </div>
          <p className="text-xl md:text-2xl text-orange-100 mb-8 max-w-4xl mx-auto leading-relaxed">
            Join the elite network of premium exporters and importers. Access exclusive features, 
            dedicated support, and advanced tools designed for serious international trade.
          </p>
          <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-6 justify-center">
            <button
              onClick={() => setActiveTab('seller')}
              className={`px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 ${
                activeTab === 'seller'
                  ? 'bg-white text-orange-600 shadow-lg transform scale-105'
                  : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              For Sellers & Exporters
            </button>
            <button
              onClick={() => setActiveTab('buyer')}
              className={`px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 ${
                activeTab === 'buyer'
                  ? 'bg-white text-orange-600 shadow-lg transform scale-105'
                  : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              For Buyers & Importers
            </button>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-12 bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {trustIndicators.map((indicator, index) => (
              <div key={index} className="text-center">
                <div className="bg-white p-4 rounded-lg shadow-sm mb-3">
                  <indicator.icon className="h-8 w-8 text-orange-600 mx-auto" />
                </div>
                <div className="text-sm font-semibold text-gray-900">{indicator.text}</div>
                <div className="text-xs text-gray-600">{indicator.subtext}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Exclusive Features for {activeTab === 'seller' ? 'Sellers' : 'Buyers'}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {activeTab === 'seller' 
                ? 'Unlock premium tools and services designed to accelerate your export business growth'
                : 'Access advanced procurement tools and services for efficient international sourcing'
              }
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {(activeTab === 'seller' ? sellerFeatures : buyerFeatures).map((feature, index) => (
              <div key={index} className="group bg-white p-8 rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-orange-200">
                <div className={`w-16 h-16 rounded-lg ${feature.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <feature.icon className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Plans */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Choose Your {activeTab === 'seller' ? 'Seller' : 'Buyer'} Plan
            </h2>
            <p className="text-xl text-gray-600">
              Select the perfect plan to match your business needs and growth goals
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {(activeTab === 'seller' ? sellerPlans : buyerPlans).map((plan, index) => (
              <div key={index} className={`relative bg-white rounded-2xl shadow-lg border-2 ${plan.color} ${plan.popular ? 'transform scale-105' : ''} transition-all duration-300 hover:shadow-xl`}>
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-2 rounded-full text-sm font-semibold">
                      Most Popular
                    </span>
                  </div>
                )}
                
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                  <p className="text-gray-600 mb-6">{plan.description}</p>
                  
                  <div className="mb-8">
                    <span className="text-4xl font-bold text-gray-900">{plan.price}</span>
                    <span className="text-gray-600">{plan.period}</span>
                  </div>

                  <ul className="space-y-4 mb-8">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center">
                        <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <button className={`w-full py-3 px-6 rounded-lg font-semibold transition-all duration-300 ${
                    plan.popular
                      ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white hover:from-orange-600 hover:to-red-600 transform hover:scale-105'
                      : 'bg-gray-900 text-white hover:bg-gray-800'
                  }`}>
                    {plan.cta}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Feature Comparison</h2>
            <p className="text-xl text-gray-600">
              See what's included in each plan at a glance
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Features</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">Standard/Basic</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">Premium/Professional</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900 bg-orange-50">Exclusive</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {activeTab === 'seller' ? (
                    <>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">Verified Badge</td>
                        <td className="px-6 py-4 text-sm text-gray-600 text-center">❌</td>
                        <td className="px-6 py-4 text-sm text-gray-600 text-center">❌</td>
                        <td className="px-6 py-4 text-sm text-gray-900 text-center bg-orange-50 font-medium">✅</td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">Featured Listings</td>
                        <td className="px-6 py-4 text-sm text-gray-600 text-center">❌</td>
                        <td className="px-6 py-4 text-sm text-gray-600 text-center">Limited</td>
                        <td className="px-6 py-4 text-sm text-gray-900 text-center bg-orange-50 font-medium">⭐ Top Priority</td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">AI Assistant</td>
                        <td className="px-6 py-4 text-sm text-gray-600 text-center">❌</td>
                        <td className="px-6 py-4 text-sm text-gray-600 text-center">Basic</td>
                        <td className="px-6 py-4 text-sm text-gray-900 text-center bg-orange-50 font-medium">24/7 Advanced</td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">Product Uploads</td>
                        <td className="px-6 py-4 text-sm text-gray-600 text-center">50</td>
                        <td className="px-6 py-4 text-sm text-gray-600 text-center">500</td>
                        <td className="px-6 py-4 text-sm text-gray-900 text-center bg-orange-50 font-medium">Unlimited</td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">RFQ Priority</td>
                        <td className="px-6 py-4 text-sm text-gray-600 text-center">❌</td>
                        <td className="px-6 py-4 text-sm text-gray-600 text-center">❌</td>
                        <td className="px-6 py-4 text-sm text-gray-900 text-center bg-orange-50 font-medium">✅ First Access</td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">Escrow Support</td>
                        <td className="px-6 py-4 text-sm text-gray-600 text-center">❌</td>
                        <td className="px-6 py-4 text-sm text-gray-600 text-center">❌</td>
                        <td className="px-6 py-4 text-sm text-gray-900 text-center bg-orange-50 font-medium">✅</td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">Hospitality Services</td>
                        <td className="px-6 py-4 text-sm text-gray-600 text-center">❌</td>
                        <td className="px-6 py-4 text-sm text-gray-600 text-center">❌</td>
                        <td className="px-6 py-4 text-sm text-gray-900 text-center bg-orange-50 font-medium">✅</td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">Inspection Assistance</td>
                        <td className="px-6 py-4 text-sm text-gray-600 text-center">❌</td>
                        <td className="px-6 py-4 text-sm text-gray-600 text-center">❌</td>
                        <td className="px-6 py-4 text-sm text-gray-900 text-center bg-orange-50 font-medium">✅</td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">Sample Management</td>
                        <td className="px-6 py-4 text-sm text-gray-600 text-center">❌</td>
                        <td className="px-6 py-4 text-sm text-gray-600 text-center">Basic</td>
                        <td className="px-6 py-4 text-sm text-gray-900 text-center bg-orange-50 font-medium">Full Service</td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">Analytics</td>
                        <td className="px-6 py-4 text-sm text-gray-600 text-center">Basic</td>
                        <td className="px-6 py-4 text-sm text-gray-600 text-center">Advanced</td>
                        <td className="px-6 py-4 text-sm text-gray-900 text-center bg-orange-50 font-medium">Premium Dashboard</td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">Account Manager</td>
                        <td className="px-6 py-4 text-sm text-gray-600 text-center">❌</td>
                        <td className="px-6 py-4 text-sm text-gray-600 text-center">❌</td>
                        <td className="px-6 py-4 text-sm text-gray-900 text-center bg-orange-50 font-medium">✅ Dedicated</td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">Payment Speed</td>
                        <td className="px-6 py-4 text-sm text-gray-600 text-center">Standard</td>
                        <td className="px-6 py-4 text-sm text-gray-600 text-center">Faster</td>
                        <td className="px-6 py-4 text-sm text-gray-900 text-center bg-orange-50 font-medium">Priority</td>
                      </tr>
                    </>
                  ) : (
                    <>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">Pre-shipment Inspection</td>
                        <td className="px-6 py-4 text-sm text-gray-600 text-center">❌</td>
                        <td className="px-6 py-4 text-sm text-gray-600 text-center">❌</td>
                        <td className="px-6 py-4 text-sm text-gray-900 text-center bg-orange-50 font-medium">✅</td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">Escrow Protection</td>
                        <td className="px-6 py-4 text-sm text-gray-600 text-center">❌</td>
                        <td className="px-6 py-4 text-sm text-gray-600 text-center">❌</td>
                        <td className="px-6 py-4 text-sm text-gray-900 text-center bg-orange-50 font-medium">✅</td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">Buy for Me Service</td>
                        <td className="px-6 py-4 text-sm text-gray-600 text-center">❌</td>
                        <td className="px-6 py-4 text-sm text-gray-600 text-center">❌</td>
                        <td className="px-6 py-4 text-sm text-gray-900 text-center bg-orange-50 font-medium">✅</td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">Sample Support</td>
                        <td className="px-6 py-4 text-sm text-gray-600 text-center">❌</td>
                        <td className="px-6 py-4 text-sm text-gray-600 text-center">Basic</td>
                        <td className="px-6 py-4 text-sm text-gray-900 text-center bg-orange-50 font-medium">Full Service</td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">Translation Support</td>
                        <td className="px-6 py-4 text-sm text-gray-600 text-center">❌</td>
                        <td className="px-6 py-4 text-sm text-gray-600 text-center">Basic</td>
                        <td className="px-6 py-4 text-sm text-gray-900 text-center bg-orange-50 font-medium">Real-time</td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">Factory Visits</td>
                        <td className="px-6 py-4 text-sm text-gray-600 text-center">❌</td>
                        <td className="px-6 py-4 text-sm text-gray-600 text-center">❌</td>
                        <td className="px-6 py-4 text-sm text-gray-900 text-center bg-orange-50 font-medium">✅ Full Coordination</td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">Trade Finance</td>
                        <td className="px-6 py-4 text-sm text-gray-600 text-center">❌</td>
                        <td className="px-6 py-4 text-sm text-gray-600 text-center">Guidance</td>
                        <td className="px-6 py-4 text-sm text-gray-900 text-center bg-orange-50 font-medium">Full Assistance</td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">Seller Matching</td>
                        <td className="px-6 py-4 text-sm text-gray-600 text-center">Standard</td>
                        <td className="px-6 py-4 text-sm text-gray-600 text-center">Priority</td>
                        <td className="px-6 py-4 text-sm text-gray-900 text-center bg-orange-50 font-medium">Best Performers</td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">Verified Access</td>
                        <td className="px-6 py-4 text-sm text-gray-600 text-center">All Suppliers</td>
                        <td className="px-6 py-4 text-sm text-gray-600 text-center">Verified Only</td>
                        <td className="px-6 py-4 text-sm text-gray-900 text-center bg-orange-50 font-medium">Premium Network</td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">Protection Guarantee</td>
                        <td className="px-6 py-4 text-sm text-gray-600 text-center">❌</td>
                        <td className="px-6 py-4 text-sm text-gray-600 text-center">Limited</td>
                        <td className="px-6 py-4 text-sm text-gray-900 text-center bg-orange-50 font-medium">Full Coverage</td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Success Stories</h2>
            <p className="text-xl text-gray-600">
              See how exclusive members are transforming their businesses
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                name: 'Bengal Textiles Ltd.',
                role: 'Exclusive Seller',
                image: 'https://images.pexels.com/photos/1043471/pexels-photo-1043471.jpeg',
                quote: 'The AI assistant and featured listings increased our inquiries by 400%. The dedicated account manager helped us expand to 12 new countries.',
                results: '+400% Inquiries, 12 New Markets'
              },
              {
                name: 'Global Fashion Corp',
                role: 'Exclusive Buyer',
                image: 'https://images.pexels.com/photos/1043471/pexels-photo-1043471.jpeg',
                quote: 'Factory visit coordination and escrow protection gave us confidence to source from Bangladesh. The multilingual assistant was game-changing.',
                results: '60% Faster Sourcing, Zero Disputes'
              },
              {
                name: 'Eco Jute International',
                role: 'Exclusive Seller',
                image: 'https://images.pexels.com/photos/1043471/pexels-photo-1043471.jpeg',
                quote: 'Sample forwarding management and pre-shipment inspection assistance helped us close deals 3x faster with international buyers.',
                results: '3x Faster Deals, 95% Approval Rate'
              }
            ].map((story, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-lg">
                <div className="flex items-center mb-6">
                  <img 
                    src={story.image} 
                    alt={story.name}
                    className="w-16 h-16 rounded-full object-cover mr-4"
                  />
                  <div>
                    <h3 className="font-semibold text-gray-900">{story.name}</h3>
                    <p className="text-orange-600 text-sm">{story.role}</p>
                  </div>
                </div>
                <blockquote className="text-gray-700 mb-4 italic">
                  "{story.quote}"
                </blockquote>
                <div className="bg-orange-50 p-3 rounded-lg">
                  <p className="text-sm font-semibold text-orange-800">{story.results}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-orange-600 to-red-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Crown className="h-20 w-20 text-yellow-300 mx-auto mb-8" />
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Join the Elite?
          </h2>
          <p className="text-xl text-orange-100 mb-8 leading-relaxed">
            Take your {activeTab === 'seller' ? 'export business' : 'sourcing operations'} to the next level with exclusive features, 
            dedicated support, and premium tools designed for serious international trade.
          </p>
          
          <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-6 justify-center mb-8">
            <Link 
              to="/create-account"
              className="bg-white text-orange-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors flex items-center justify-center"
            >
              <Crown className="h-5 w-5 mr-2" />
              Become Exclusive Now
              <ArrowRight className="h-5 w-5 ml-2" />
            </Link>
            <button className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-orange-600 transition-colors flex items-center justify-center">
              <MessageCircle className="h-5 w-5 mr-2" />
              Schedule Consultation
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            <div className="flex items-center justify-center">
              <Phone className="h-5 w-5 mr-2" />
              <span>+88 01770 156 736</span>
            </div>
            <div className="flex items-center justify-center">
              <Mail className="h-5 w-5 mr-2" />
              <span>hello@madeinbd.com</span>
            </div>
            <div className="flex items-center justify-center">
              <MapPin className="h-5 w-5 mr-2" />
              <span>57/8, Ground floor, East Rajabzar, West Panthapath, Dhaka 1215, Bangladesh.</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BecomeExclusivePage;